import React, { useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import { useDispatch } from 'react-redux';
import { setActivityDrawer } from '@store/appStore';
import { ExpenseGroup } from '@api/CashFlows/CashFlow';
import { EXPENSE_GROUPS, ExpenseCategory } from '@api/ExpenseCategories/ExpenseCategory';
import ExpenseCategoryServ, { EXPENSE_CATEGORIES_ACTIVITY } from '@api/ExpenseCategories/ExpenseCategoryServ';
import ExpenseGroupChip from '@components/Expenses/ExpenseGroupChip';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';

/**
 * Categorías de gasto (plan de Gastos F3, solo el admin general). Renombrar,
 * desactivar y agregar. **No se borran**: cada gasto guarda copia del nombre y del
 * grupo, así que renombrar no reescribe la historia. El grupo de una categoría que
 * ya existe no cambia (movería lo histórico de renglón en el Estado de Resultados).
 * Lecturas: las ~25 categorías (caché de sesión; guardar la invalida).
 */
const ExpenseCategoriesPanel: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const [categories, setCategories] = useState<ExpenseCategory[]>([]);
    const [editing, setEditing] = useState<{ category: ExpenseCategory; previous?: ExpenseCategory } | undefined>(undefined);

    const load = () =>
        ExpenseCategoryServ.getExpenseCategories()
            .then(setCategories)
            .catch(showAlertError);

    useEffect(() => {
        void load();
    }, []);

    const save = async () => {
        if (!editing) return;
        showLoader();
        try {
            await ExpenseCategoryServ.saveExpenseCategory(editing.category, editing.previous);
            showAlertMessage('Categoría guardada');
            setEditing(undefined);
            await load();
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    const toggle = async (category: ExpenseCategory) => {
        showLoader();
        try {
            await ExpenseCategoryServ.saveExpenseCategory({ ...category, active: !category.active }, category);
            await load();
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    const startNew = (group: ExpenseGroup) => {
        const inGroup = categories.filter((category) => category.group === group);
        setEditing({
            category: {
                id: ExpenseCategoryServ.newExpenseCategoryId(),
                name: '',
                group,
                active: true,
                order: inGroup.reduce((max, category) => Math.max(max, category.order || 0), 0) + 1,
                ...(group === ExpenseGroup.Fixed || group === ExpenseGroup.Variable ? { paidFromCashByDefault: false } : {})
            }
        });
    };

    return (
        <Stack height="100%" minHeight={0} spacing={2} p={2} boxSizing="border-box" sx={{ overflowY: 'auto' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexShrink={0}>
                <Typography variant="body2" color="text.secondary">
                    Cada categoría pertenece a un renglón del Estado de Resultados. Desactivar la esconde al capturar; los gastos ya
                    registrados no cambian.
                </Typography>
                <Button
                    startIcon={<HistoryIcon />}
                    onClick={() => dispatch(setActivityDrawer({ isOpen: true, ...EXPENSE_CATEGORIES_ACTIVITY }))}
                >
                    Actividad
                </Button>
            </Stack>
            {EXPENSE_GROUPS.map((info) => (
                <Stack key={info.group} spacing={1} flexShrink={0}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <ExpenseGroupChip group={info.group} />
                        <Button size="small" startIcon={<AddIcon />} onClick={() => startNew(info.group)}>
                            Agregar
                        </Button>
                    </Stack>
                    {categories
                        .filter((category) => category.group === info.group)
                        .map((category) => (
                            <Stack
                                key={category.id}
                                direction="row"
                                alignItems="center"
                                spacing={2}
                                pl={2}
                                sx={{ opacity: category.active ? 1 : 0.5 }}
                            >
                                <Typography flex={1}>{category.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {[
                                        category.askCollaborator ? 'pide colaborador' : '',
                                        category.askInput ? 'pide insumo' : '',
                                        category.paidFromCashByDefault === false ? 'arranca "con otro dinero"' : ''
                                    ]
                                        .filter(Boolean)
                                        .join(' · ')}
                                </Typography>
                                <FormControlLabel
                                    control={<Switch size="small" checked={category.active} onChange={() => void toggle(category)} />}
                                    label={category.active ? 'Activa' : 'Inactiva'}
                                />
                                <Tooltip title="Editar">
                                    <IconButton
                                        size="small"
                                        aria-label={`Editar ${category.name}`}
                                        onClick={() => setEditing({ category: { ...category }, previous: category })}
                                    >
                                        <EditOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        ))}
                </Stack>
            ))}

            <Dialog open={!!editing} onClose={() => setEditing(undefined)} fullWidth maxWidth="xs" PaperProps={{ style: { borderRadius: 15 } }}>
                <DialogTitle fontWeight={600}>{editing?.previous ? 'Editar categoría' : 'Nueva categoría'}</DialogTitle>
                <DialogContent>
                    {editing && (
                        <Stack spacing={2} pt={1}>
                            <TextField
                                autoFocus
                                label="Nombre"
                                value={editing.category.name}
                                onChange={(event) =>
                                    setEditing({ ...editing, category: { ...editing.category, name: event.target.value } })
                                }
                            />
                            <FormControl disabled={!!editing.previous}>
                                <InputLabel id="category-group-label">Renglón del Estado de Resultados</InputLabel>
                                <Select
                                    labelId="category-group-label"
                                    label="Renglón del Estado de Resultados"
                                    value={editing.category.group}
                                    onChange={(event) =>
                                        setEditing({
                                            ...editing,
                                            category: { ...editing.category, group: event.target.value as ExpenseGroup }
                                        })
                                    }
                                >
                                    {EXPENSE_GROUPS.map((info) => (
                                        <MenuItem key={info.group} value={info.group}>
                                            {info.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={editing.category.paidFromCashByDefault !== false}
                                        onChange={(event) =>
                                            setEditing({
                                                ...editing,
                                                category: { ...editing.category, paidFromCashByDefault: event.target.checked }
                                            })
                                        }
                                    />
                                }
                                label='"Salió de la caja de este turno" arranca encendido'
                            />
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditing(undefined)}>Cancelar</Button>
                    <Button variant="contained" onClick={() => void save()}>
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default ExpenseCategoriesPanel;
